import React, { useState } from 'react';

import { Badge, Button, Icon, Input } from '../../../../core/ui';
import { useTranslation } from '../../../../core/context/TranslationContext';
import { useToast } from '../../../../core/context/ToastContext';

import { confirmTransactionAmount, updateTransactionAmount } from '../../api/budget';
import { formatTry, parseAmountInput } from '../../format';
import styles from './AmountCell.module.css';

/**
 * Amount column of a statement row. An amount flagged for review can be
 * corrected in place or confirmed as it is; both clear the flag.
 */
export default function AmountCell({ transaction, onUpdated }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (request, successKey) => {
    setSaving(true);
    try {
      onUpdated(await request());
      setEditing(false);
      toast(t(successKey), { variant: 'success' });
    } catch (err) {
      console.error('Amount update failed', err);
      toast(t('budget.amountUpdateFailed'), { variant: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setDraft(String(transaction.amount ?? ''));
    setInvalid(false);
    setEditing(true);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const amount = parseAmountInput(draft);
    if (amount === null) {
      setInvalid(true);
      return;
    }
    save(() => updateTransactionAmount(transaction.id, amount), 'budget.amountCorrected');
  };

  const onConfirm = () =>
    save(() => confirmTransactionAmount(transaction.id), 'budget.amountConfirmed');

  if (editing) {
    return (
      <form className={styles.form} onSubmit={onSubmit} onClick={(e) => e.stopPropagation()}>
        <Input
          className={styles.input}
          aria-label={t('budget.amountInputLabel')}
          inputMode="decimal"
          autoFocus
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setInvalid(false);
          }}
          onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
          error={invalid ? t('budget.amountInvalid') : undefined}
        />
        <div className={styles.actions}>
          <Button size="sm" type="submit" disabled={saving}>{t('save')}</Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
            {t('cancel')}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div>
      <div>{formatTry(transaction.amount)}</div>
      {transaction.amountNeedsReview && (
        <>
          <Badge variant="warning" className={styles.badge} title={t('budget.amountNeedsReviewHint')}>
            <Icon name="fa-alert-circle" size="sm" />
            {t('budget.amountNeedsReview')}
          </Badge>
          <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="secondary" onClick={startEditing} disabled={saving}>
              <Icon name="fa-pencil" size="sm" />
              {t('budget.correctAmount')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onConfirm} disabled={saving}>
              <Icon name="fa-check" size="sm" />
              {t('budget.confirmAmount')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
